import { Box, Container, Grid, Stack, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { motion } from 'framer-motion';

import site from '../config/site';
import { litSurface, seam } from '../styles/surfaces';
import SectionHeading from './common/SectionHeading';
import Icon from './common/Icon';

/**
 * Sección "Lo que vas a lograr".
 *
 * Ocupa el lugar donde antes había testimonios. Mientras no haya reseñas
 * reales de clientes, esta sección explica el cambio concreto en el día a día
 * del negocio (antes / ahora) en vez de poner frases en boca de nadie.
 */

/** Una línea del contraste: el punto gris es el "antes"; el verde, el "ahora". */
function ContrastLine({ label, text, color, dim }) {
  return (
    <Stack direction="row" spacing={1.4}>
      <Box
        sx={{
          width: 9,
          height: 9,
          borderRadius: '50%',
          flexShrink: 0,
          mt: '7px',
          bgcolor: dim ? alpha(color, 0.35) : color,
        }}
      />
      <Box>
        <Typography
          component="span"
          sx={{
            display: 'block',
            fontSize: '0.72rem',
            fontWeight: 700,
            letterSpacing: '0.14em',
            textTransform: 'uppercase',
            color: dim ? 'text.secondary' : color,
            mb: 0.4,
          }}
        >
          {label}
        </Typography>
        <Typography
          sx={{
            fontSize: '0.94rem',
            lineHeight: 1.65,
            color: dim ? 'text.secondary' : 'text.primary',
          }}
        >
          {text}
        </Typography>
      </Box>
    </Stack>
  );
}

export default function Outcomes() {
  const theme = useTheme();
  const green = theme.palette.primary.main;
  const { outcomes } = site;

  if (!outcomes.enabled || outcomes.items.length === 0) return null;

  const columns = Math.min(outcomes.items.length, 3);

  return (
    <Box
      component="section"
      id="outcomes"
      sx={{ py: { xs: 8, md: 11 }, background: seam(site.colors.backgroundDeep, site.colors.background) }}
    >
      <Container maxWidth="lg">
        <SectionHeading eyebrow={outcomes.eyebrow} title={outcomes.title} subtitle={outcomes.subtitle} />

        <Grid container spacing={{ xs: 3, md: 3.5 }} justifyContent="center">
          {outcomes.items.map((item, index) => (
            <Grid key={item.title} size={{ xs: 12, sm: 6, md: 12 / columns }}>
              <Stack
                component={motion.div}
                initial={{ opacity: 0, y: 28 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                sx={{
                  height: '100%',
                  p: { xs: 3, md: 3.5 },
                  borderRadius: 4,
                  ...litSurface(),
                  transition: 'background .3s ease',
                  '&:hover': {
                    background: `linear-gradient(180deg, ${alpha('#FFFFFF', 0.075)}, ${alpha('#FFFFFF', 0.02)})`,
                  },
                }}
              >
                <Icon name={item.icon} sx={{ fontSize: 30, color: green, mb: 2 }} />

                <Typography variant="h6" sx={{ fontSize: '1.12rem', mb: 2.4 }}>
                  {item.title}
                </Typography>

                <Stack spacing={2}>
                  {item.before && <ContrastLine label="Antes" text={item.before} color={green} dim />}
                  {item.after && <ContrastLine label="Ahora" text={item.after} color={green} />}
                </Stack>
              </Stack>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
}
